<template>
  <div ref="wrap" :style="computedWrapStyle">
    <div ref="content" :style="computedContentStyle">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
const wrapEl = useTemplateRef('wrap')
const contentEl = useTemplateRef('content')

const emit = defineEmits(['enter', 'entered', 'exit', 'exited'])

const props = withDefaults(
  defineProps<{
    appear?: boolean | number
    collapsedHeight?: string
    collapsedOpacity?: number
    ease?: string
    fadeIn?: boolean
    inProp?: boolean
    offsetContent?: boolean | string
    timeout?: number
  }>(),
  {
    appear: false,
    collapsedHeight: '0px',
    collapsedOpacity: 0,
    ease: 'ease',
    fadeIn: false,
    inProp: false,
    offsetContent: false,
    timeout: 400,
  },
)

const wrapOverflow = ref('hidden')
const contentOverflow = ref('auto')

// this will get overwritten by expand / collapse methods
const computedWrapStyle = computed(() => {
  return {
    height: props.appear ? 'auto' : props.collapsedHeight,
    overflow: wrapOverflow.value,
    transition: `height ${props.timeout}ms ${props.ease}`,
    willChange: 'height',
  }
})

const computedContentStyle = computed(() => {
  let style = computedDefaultContentStyle.value
  if (props.fadeIn) {
    style = {
      ...style,
      ...computedFadeInStyle.value,
    }
  }
  if (props.offsetContent) {
    style = {
      ...style,
      ...computedOffsetStyle.value,
    }
  }
  return style
})

const computedDefaultContentStyle = computed(() => {
  return {
    overflow: contentOverflow.value, // handle Collapsing Margins
    transitionDuration:
      props.fadeIn || props.offsetContent ? `${props.timeout}ms` : undefined,
    transitionTimingFunction:
      props.fadeIn || props.offsetContent ? `${props.ease}` : undefined,
    transitionProperty: computedTransitionProps.value,
    willChange: computedTransitionProps.value,
  }
})

const computedFadeInStyle = computed(() => {
  return {
    opacity: props.inProp ? 1 : props.collapsedOpacity,
  }
})

const computedOffsetStyle = computed(() => {
  return {
    transform: props.inProp
      ? 'translate3d(0, 0, 0)'
      : computedOffsetValue.value,
  }
})

const computedOffsetValue = computed(() => {
  switch (props.offsetContent) {
    case true:
      return 'translateY(-100%)'
    case false:
    case !props.offsetContent:
      return undefined
    default:
      return props.offsetContent
  }
})

const computedTransitionProps = computed(() => {
  let property
  if (props.fadeIn) {
    property = 'opacity'
  }
  if (props.offsetContent) {
    property = property !== undefined ? `${property}, transform` : 'transform'
  }
  return property
})

const setWrapHeight = () => {
  if (wrapEl.value) {
    emit(props.inProp ? 'entered' : 'exited', wrapEl.value)
    wrapEl.value.style.height = props.inProp ? 'auto' : props.collapsedHeight
  }
}

const syncWrapAndContentHeight = () => {
  console.log('hello from syncWrapAndContentHeight')
  if (contentEl.value?.getBoundingClientRect && wrapEl.value) {
    const contentHeight = `${contentEl.value.getBoundingClientRect().height}px`
    wrapEl.value.style.height = contentHeight
  }
}

const setWrapHeightToCollapsedHeight = () => {
  if (wrapEl.value) wrapEl.value.style.height = props.collapsedHeight
}

const onTransitionend = (e: TransitionEvent) => {
  if (e.target === wrapEl.value) {
    setWrapHeight()
    handleOverflow()
  }
  return undefined
}

const handleOverflow = () => {
  wrapOverflow.value = props.inProp ? 'visible' : 'hidden'

  contentOverflow.value = props.inProp ? 'visible' : 'auto'
}

const expand = () => {
  console.log('hello from expand')
  emit('enter', wrapEl.value)
  syncWrapAndContentHeight()
}

const collapse = () => {
  emit('exit', wrapEl.value)
  syncWrapAndContentHeight()

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      setWrapHeightToCollapsedHeight()
      handleOverflow()
    })
  })
}

watch(
  () => props.inProp,
  (isIn) => {
    console.log(isIn)
    if (isIn) {
      expand()
    } else {
      collapse()
    }
  },
)

onMounted(() => {
  wrapEl.value?.addEventListener('transitionend', onTransitionend)

  handleOverflow()

  if (props.inProp && !props.appear) {
    setWrapHeight()
  }

  if (props.inProp && props.appear) {
    switch (typeof props.appear) {
      case 'boolean':
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            expand()
          })
        })
        break
      case 'number':
        setTimeout(() => {
          expand()
        }, props.appear)
        break
      default:
        try {
          throw new Error(
            `Expect "appear" prop to be either Boolean or Number... \nGot ${typeof props.appear} with value "${
              props.appear
            }"... \nTreating it as "appear" set to false`,
          )
        } catch (e) {
          console.warn(e)
          setWrapHeight()
        }
        break
    }
  }
})

onBeforeUnmount(() => {
  wrapEl.value?.removeEventListener('transitionend', onTransitionend)
})
</script>
